import { Point } from '@/types/common';
import { logger } from '@/lib/logger';

export interface SegmentationMask {
  y0: number; // in [0..height - 1]
  x0: number; // in [0..width - 1]
  y1: number; // in [0..height - 1]
  x1: number; // in [0..width - 1]
  mask: string; // base64 encoded PNG
  label: string;
  confidence?: number;
}

export interface GeminiResponse {
  box_2d: number[]; // [y0, x0, y1, x1] in normalized coordinates (0-1000)
  mask: string; // base64 PNG with data URI prefix
  label: string;
}

export interface ProcessedRoom {
  polygon: Point[];
  segmentation_mask: string;
  room_type: string;
  label: string;
  confidence: number;
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  features: string[];
}

/**
 * Parse JSON response from Gemini, handling markdown fencing
 */
export function parseGeminiJSON(jsonOutput: string): any {
  // Remove markdown fencing if present
  const lines = jsonOutput.split('\n');
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim() === '```json') {
      jsonOutput = lines.slice(i + 1).join('\n');
      const endIndex = jsonOutput.indexOf('```');
      if (endIndex !== -1) {
        jsonOutput = jsonOutput.substring(0, endIndex);
      }
      break;
    }
  }
  
  try {
    return JSON.parse(jsonOutput.trim());
  } catch (error) {
    logger.error('Failed to parse Gemini response:', error);
    throw new Error('Invalid JSON response from Gemini');
  }
}

/**
 * Parse segmentation masks from Gemini response
 */
export function parseSegmentationMasks(
  responseText: string,
  imageWidth: number,
  imageHeight: number
): ProcessedRoom[] {
  const items = parseGeminiJSON(responseText);
  const processedRooms: ProcessedRoom[] = [];

  for (const item of items) {
    if (!item.box_2d || !item.mask || !item.label) {
      logger.warn('Skipping invalid item:', item);
      continue;
    }

    // Convert normalized coordinates to absolute pixels
    const y0 = Math.round((item.box_2d[0] / 1000) * imageHeight);
    const x0 = Math.round((item.box_2d[1] / 1000) * imageWidth);
    const y1 = Math.round((item.box_2d[2] / 1000) * imageHeight);
    const x1 = Math.round((item.box_2d[3] / 1000) * imageWidth);

    // Validate bounds
    if (y0 >= y1 || x0 >= x1) {
      logger.warn('Invalid bounding box:', item.box_2d);
      continue;
    }

    // Extract room type from label
    const roomType = extractRoomType(item.label);
    const features = extractRoomFeatures(item.label);

    // Create polygon from bounding box (will be refined later from mask)
    const polygon: Point[] = [
      { x: x0, y: y0 },
      { x: x1, y: y0 },
      { x: x1, y: y1 },
      { x: x0, y: y1 }
    ];

    // Clean mask data (remove data URI prefix if present)
    let maskData = item.mask;
    if (maskData.startsWith('data:image/png;base64,')) {
      maskData = maskData.substring('data:image/png;base64,'.length);
    }

    processedRooms.push({
      polygon,
      segmentation_mask: maskData,
      room_type: roomType,
      label: item.label,
      confidence: item.confidence || 0.95, // Gemini doesn't always provide confidence
      bounds: {
        x: x0,
        y: y0,
        width: x1 - x0,
        height: y1 - y0
      },
      features
    });
  }

  return processedRooms;
}

/**
 * Extract room type from Gemini's descriptive label
 */
export function extractRoomType(label: string): string {
  const lowerLabel = label.toLowerCase();
  
  // Common room type mappings
  const roomTypes: { [key: string]: string[] } = {
    'bedroom': ['bedroom', 'bed room', 'master bedroom', 'guest bedroom', 'child bedroom'],
    'bathroom': ['bathroom', 'bath', 'toilet', 'wc', 'washroom', 'restroom', 'powder room'],
    'kitchen': ['kitchen', 'cooking area', 'pantry'],
    'living_room': ['living room', 'living', 'lounge', 'family room', 'sitting room'],
    'dining_room': ['dining room', 'dining', 'eating area'],
    'office': ['office', 'study', 'workspace', 'home office', 'den'],
    'garage': ['garage', 'parking', 'carport'],
    'hallway': ['hallway', 'corridor', 'passage', 'foyer', 'entryway', 'entrance'],
    'closet': ['closet', 'wardrobe', 'storage', 'walk-in'],
    'laundry': ['laundry', 'utility room', 'mudroom'],
    'balcony': ['balcony', 'terrace', 'patio', 'deck'],
    'stairs': ['stairs', 'staircase', 'stairwell']
  };

  for (const [type, keywords] of Object.entries(roomTypes)) {
    for (const keyword of keywords) {
      if (lowerLabel.includes(keyword)) {
        return type;
      }
    }
  }

  return 'unknown';
}

/**
 * Extract features from room label
 */
export function extractRoomFeatures(label: string): string[] {
  const features: string[] = [];
  const lowerLabel = label.toLowerCase();

  // Size features
  if (lowerLabel.includes('master') || lowerLabel.includes('large')) {
    features.push('large');
  }
  if (lowerLabel.includes('small') || lowerLabel.includes('compact')) {
    features.push('small');
  }

  // Position features
  if (lowerLabel.includes('corner')) {
    features.push('corner');
  }
  if (lowerLabel.includes('central')) {
    features.push('central');
  }

  // Special features
  if (lowerLabel.includes('walk-in') || lowerLabel.includes('walkin')) {
    features.push('walk-in');
  }
  if (lowerLabel.includes('ensuite') || lowerLabel.includes('en-suite')) {
    features.push('ensuite');
  }
  if (lowerLabel.includes('guest')) {
    features.push('guest');
  }
  if (lowerLabel.includes('open')) {
    features.push('open-plan');
  }

  return features;
}

/**
 * Convert a segmentation mask to polygon points
 * This is a simplified version - in production you'd want proper contour detection
 */
export async function maskToPolygon(
  maskBase64: string,
  bounds: { x: number; y: number; width: number; height: number }
): Promise<Point[]> {
  // For now, return the bounding box as polygon
  // In a full implementation, you would:
  // 1. Decode the base64 PNG
  // 2. Find contours in the mask
  // 3. Simplify the contour to a polygon
  // 4. Transform to image coordinates
  
  return [
    { x: bounds.x, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y },
    { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
    { x: bounds.x, y: bounds.y + bounds.height }
  ];
}

/**
 * Calculate IoU (Intersection over Union) for two bounding boxes
 */
export function calculateIoU(
  box1: { x: number; y: number; width: number; height: number },
  box2: { x: number; y: number; width: number; height: number }
): number {
  const x1 = Math.max(box1.x, box2.x);
  const y1 = Math.max(box1.y, box2.y);
  const x2 = Math.min(box1.x + box1.width, box2.x + box2.width);
  const y2 = Math.min(box1.y + box1.height, box2.y + box2.height);

  if (x2 < x1 || y2 < y1) {
    return 0; // No overlap
  }

  const intersection = (x2 - x1) * (y2 - y1);
  const area1 = box1.width * box1.height;
  const area2 = box2.width * box2.height;
  const union = area1 + area2 - intersection;

  return union > 0 ? intersection / union : 0;
}

/**
 * Merge overlapping room detections
 */
export function mergeOverlappingRooms(
  rooms: ProcessedRoom[],
  iouThreshold: number = 0.3
): ProcessedRoom[] {
  const merged: ProcessedRoom[] = [];
  const used = new Array(rooms.length).fill(false);

  for (let i = 0; i < rooms.length; i++) {
    if (used[i]) continue;

    const group: ProcessedRoom[] = [rooms[i]];
    used[i] = true;

    // Find overlapping rooms
    for (let j = i + 1; j < rooms.length; j++) {
      if (used[j]) continue;

      const iou = calculateIoU(rooms[i].bounds, rooms[j].bounds);
      if (iou > iouThreshold) {
        group.push(rooms[j]);
        used[j] = true;
      }
    }

    // Select the room with highest confidence from the group
    const bestRoom = group.reduce((best, current) =>
      current.confidence > best.confidence ? current : best
    );

    merged.push(bestRoom);
  }

  return merged;
}

/**
 * Get color for room type visualization
 * Returns a unified soft blue for all rooms for a clean, professional look
 */
export function getRoomTypeColor(roomType: string): string {
  // Unified soft blue color for all rooms
  // #60A5FA - Tailwind blue-400: soft, professional, easy on eyes
  return '#60A5FA';
}

/**
 * Format room label for display
 */
export function formatRoomLabel(label: string, roomType: string): string {
  // If label is already descriptive, use it
  if (label && label.toLowerCase() !== roomType.replace('_', ' ')) {
    return label;
  }

  // Otherwise, format the room type
  return roomType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}